import { chain, first, snakeCase, map, sumBy, reduce, isEmpty } from "lodash"
import PropTypes from "prop-types"

export const AGGREGATION_FUNCTIONS = {
  NONE: "all",
  MAX: "max",
  SUM: "sum",
}

export const STYLE_AGGREGATION_OPTIONS = [
  {
    id: AGGREGATION_FUNCTIONS.MAX,
    optionLabelOverride: "MAX",
    primaryText: "Factors as max aggregation",
    secondaryText: "This combines the underlying linked model factors, displaying the absolute max from each factor group. Single factors may appear.",
  },
  {
    id: AGGREGATION_FUNCTIONS.NONE,
    optionLabelOverride: "ALL",
    primaryText: "Show all linked model factors",
    secondaryText: "All linked model factors are displayed, providing the underlying exposures respective to the corresponding factor model.",
  },
]
export const AGGREGATION_SHAPE = PropTypes.oneOf(map(AGGREGATION_FUNCTIONS))

function getAggregatedValue(groupAggregationFunction, values, aggregationFieldName) {
  let aggregatedValue
  switch (groupAggregationFunction) {
    case AGGREGATION_FUNCTIONS.MAX:
      aggregatedValue = reduce(values, (max, item) =>
        Math.abs(item[aggregationFieldName]) > Math.abs(max) ? item[aggregationFieldName] : max, 0)
      break
    case AGGREGATION_FUNCTIONS.SUM:
      aggregatedValue = sumBy(values, aggregationFieldName)
  }
  return aggregatedValue
}

export function aggregateFactors(classification, factors, aggregationFieldName, { isStyleFactorsAdditive = true, styleAggregation = AGGREGATION_FUNCTIONS.NONE } = {}) {
  if (isEmpty(classification)) {
    return null
  }

  let factorsMetadata = factors
  if (!Array.isArray(factors)) {
    factorsMetadata = map(factorsMetadata, (val, key) => ({ ...val, id: key }))
  }

  let aggregationFns = AGGREGATION_FUNCTIONS.SUM
  if (!isStyleFactorsAdditive) {
    aggregationFns = [
      { condition: (item) => item.category === "style", aggregationFn: styleAggregation },
      { condition: (item) => item.category !== "style", aggregationFn: AGGREGATION_FUNCTIONS.SUM },
    ]
  }

  return chain(factorsMetadata)
    .groupBy(({ id }) => classification[id] ? snakeCase(classification[id]) : id)
    .map((values, key) => {
      const common = first(values)
      const classificationLabel = classification[common.id]
      if (!classificationLabel) {
        return first(values)
      }

      let groupAggregationFunction = aggregationFns
      if (Array.isArray(aggregationFns)) {
        groupAggregationFunction = chain(aggregationFns)
          .find(fns => fns.condition(common))
          .get("aggregationFn")
          .value()
      }

      if (groupAggregationFunction === AGGREGATION_FUNCTIONS.NONE) {
        return values
      }

      // Need to aggregate the factors
      const result = {
        ...common,
        id: key,
        label: classificationLabel,
        name: classificationLabel,
        alias: classificationLabel,
        isAggregated: true,
      }

      if (groupAggregationFunction && aggregationFieldName) {
        result[aggregationFieldName] = getAggregatedValue(groupAggregationFunction, values, aggregationFieldName)
      }

      return result
    })
    .flatten()
    .value()
}

export function getAggregatedFactorsList(classification, factors, aggregatedFactorId) {
  let factorsMetadata = factors
  if (!Array.isArray(factors)) {
    factorsMetadata = map(factorsMetadata, (val, key) => ({ ...val, id: key }))
  }

  return chain(factorsMetadata)
    .groupBy(({ id }) => classification[id] ? snakeCase(classification[id]) : id)
    .get(aggregatedFactorId)
    .map(item => item.id)
    .value()
}

export function aggregateExposuresFactors(classification, factors, styleAggregation, fieldName) {
  return aggregateFactors(classification, factors, fieldName, { isStyleFactorsAdditive: false, styleAggregation })
}

export function aggregateFactorsSumBy(classification, factors, fieldName) {
  return aggregateFactors(classification, factors, fieldName, { isStyleFactorsAdditive: true })
}
